import React, { useEffect, useState } from 'react';
import DropdownWrapper from '../DropdownWrapper';
import { ListLabelSpan } from '../shared/styles';
import CreateCustomRegionMenu from 'components/base/Menu/RegionMenu/CreateCustomRegionMenu';

import { ListItem } from '../CountryDropdown/ExtendedCountryDropdown';
import DropdownButton from '../DropdownButton';
import { ICountryFlagProps, IRegionProps } from 'types/shared';
import { CUSTOM_USER_REGION_TYPE } from 'config';

interface ICustomRegionViewProps {
  countryItems: ICountryFlagProps[];
  regionNames: string[];
  selectedItem: IRegionProps;
  selectedItems: IRegionProps[];
  disabled?: boolean;
  itemToString?: (item: any) => string | null;
  isSmallScreen: boolean;
  defaultLabel?: string;
  children?: any;
  disabledMessage?: string;
  showListLabel?: boolean;
  /** Max width of the tooltip for the dropdown, zIndex of the tooltip block,
   * placement is for place of tooltip relatively the block, isStretched is to stretch the block in tooltip wrapper */
  maxWidth?: string;
  zIndex?: string;
  placement?: string;
  isStretched?: boolean;
  clearFilter: () => void;
  isOpened?: boolean;
  onClose: () => void;
  returnBack: () => void;
  onCreateRegion: (region: IRegionProps, regionId?: string) => void;
  editCustomRegion?: IRegionProps;
  onDelete: () => void;
}

const getButtonWithDefaultLabel = (
  clearFilter: () => void,
  defaultLabel?: string,
  selectedRegion?: any,
  selectedRegions?: any[]
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        selectedItems={selectedRegions}
        selectedItem={selectedRegion}
        defaultLabel={defaultLabel}
        dropdownType="Region"
        clearFilter={clearFilter}
      />
    );
  };
  return LabeledButton;
};

const CustomRegionView: React.FC<ICustomRegionViewProps> = (props) => {
  const {
    countryItems,
    regionNames,
    disabled,
    itemToString,
    isSmallScreen,
    defaultLabel,
    children,
    disabledMessage,
    isStretched,
    maxWidth,
    zIndex,
    placement,
    clearFilter,
    returnBack,
    onClose,
    isOpened,
    onCreateRegion,
    editCustomRegion,
    onDelete,
    selectedItem,
    selectedItems,
  } = props;

  const [selectedCountries, setSelectedCountries] = useState<
    ICountryFlagProps[]
  >([]);

  useEffect(() => {
    if (editCustomRegion) {
      setSelectedCountries(editCustomRegion.countries);
    }
  }, [editCustomRegion]);

  const onCountryChange = (item: any): void => {
    if (selectedCountries.length) {
      const isChecked = selectedCountries
        .map((c: any): any => c.id)
        .includes(item?.id);

      let newList = [item];

      if (isChecked && selectedCountries?.length) {
        newList = selectedCountries?.filter(
          (country: any) => country.id !== item?.id
        );
      } else if (selectedCountries?.length) {
        newList = [...selectedCountries, item];
      }
      setSelectedCountries(
        newList.sort((a, b) => a.displayName.localeCompare(b.displayName))
      );
    } else {
      setSelectedCountries([item]);
    }
  };

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const removeCountryFromCustomRegion = (item: ICountryFlagProps): void => {
    const newList = selectedCountries.filter(
      (c: ICountryFlagProps) => c.id !== item.id
    );
    setSelectedCountries(newList);
  };

  const checkIsInvalid = (name: string): boolean => {
    const isInvalid = editCustomRegion
      ? regionNames.includes(name) && editCustomRegion.displayName !== name
      : regionNames.includes(name);
    if (isInvalid) setErrorMsg('This name is already in use.');
    return isInvalid;
  };

  const onSave = (
    inputValue: string,
    selectedItems: ICountryFlagProps[]
  ): void => {
    const newRegion: IRegionProps = {
      id: editCustomRegion
        ? editCustomRegion.id
        : inputValue.toLocaleLowerCase().replace(/ /g, '_'),
      displayName: inputValue,
      countries: selectedItems,
      regionType: CUSTOM_USER_REGION_TYPE,
    };

    onCreateRegion(newRegion, editCustomRegion?.id);
  };

  return (
    <>
      <DropdownWrapper
        hasWideSearchableMenu
        isEnabled={!disabled}
        label="REGION VIEW"
      >
        <CreateCustomRegionMenu
          onClose={onClose}
          items={countryItems}
          selectedCountries={selectedCountries}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onChange={(): void => {}}
          onSelect={onCountryChange}
          disabled={disabled}
          itemToString={itemToString}
          Button={getButtonWithDefaultLabel(
            clearFilter,
            defaultLabel,
            selectedItem,
            selectedItems
          )}
          Item={ListItem}
          isOpenDropdownDef={isOpened}
          ListLabel={ListLabelSpan}
          disabledMessage={disabledMessage}
          isSmallScreen={isSmallScreen}
          maxWidth={maxWidth}
          zIndex={zIndex}
          placement={placement}
          isMenuButtonStretched={isStretched}
          textToSelect="REGION"
          isMultipleSelection
          returnBack={returnBack}
          onRemoveItem={(item: ICountryFlagProps): void =>
            removeCountryFromCustomRegion(item)
          }
          checkIsInvalidInput={(name: string): boolean => checkIsInvalid(name)}
          inputPlaceholder="Enter region name"
          inputErrorMessage={errorMsg}
          onSave={onSave}
          onDelete={onDelete}
          editedRegion={editCustomRegion}
        >
          {children}
        </CreateCustomRegionMenu>
      </DropdownWrapper>
    </>
  );
};

export default CustomRegionView;
